import { useRef, useEffect, useState } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import Corporate from '../../assets/images/corporate.png'
import Film from '../../assets/images/film.jpeg'
import Flight from '../../assets/images/flight-2.png'
import London from '../../assets/images/london.png'
import subcontract from '../../assets/images/subcontractors.png'
import Wedding from '../../assets/images/wedding.png'
import PrivateJetImg from '../../assets/images/private-jet.png'
import CruiseImg from '../../assets/images/cruise.jpeg'
import style from './Service.module.scss'

interface ServiceConfig {
  id: string
  image: string
  title: string
  description: string
  alt: string
  titleTag: string
  link: string
}

const servicesConfig: ServiceConfig[] = [
  {
    id: 'weddings',
    image: PrivateJetImg,
    title: 'Private Jet Chauffeur Service London',
    description:
      "Your journey doesn't end when your jet touches down — it continues seamlessly on the ground. Our Executive Jet Chauffeur Service London ensures that every leg of your travel is marked by elegance, comfort, and efficiency. We provide premium chauffeur service for private jet passengers London.",
    alt: 'Private Jet Chauffeur Service London',
    titleTag: 'Private Jet Chauffeur Service London',
    link: '/services/PrivateJetChauffeurServiceLondon',
  },
  {
    id: 'airport',
    image: Flight,
    title: 'Airport Chauffeur Service London',
    description:
      'Your personal chauffeur will track your flight, assist with luggage, and provide a smooth, stress-free journey to or from the airport. Our executive airport transfer service London and chauffeur driven airport transfers London turn routine transfers into a first-class experience, including Heathrow airport private chauffeur London.',
    alt: 'Airport Chauffeur Service London',
    titleTag: 'Airport Chauffeur Service London',
    link: '/services/AirportJetChauffeurServiceLondon',
  },
  {
    id: 'corporate',
    image: Corporate,
    title: 'Corporate Chauffeur Services London',
    description:
      "Whether it's an important meeting, a business event, or a day of back-to-back appointments, our Executive Chauffeur Service London ensures you arrive prepared, on time, and in comfort. Our chauffeur service for corporate clients London provides luxury vehicles equipped for productivity and relaxation.",
    alt: 'Corporate Chauffeur Service London',
    titleTag: 'Corporate Chauffeur Service London',
    link: '/services/CorporateJetChauffeurServiceLondon',
  },
  {
    id: 'subcontractors',
    image: CruiseImg,
    title: 'Cruise Port Chauffeur Transfers London',
    description:
      'We provide luxury chauffeur to cruise terminal London, ensuring seamless, stress-free transfers and optional personalized shore experiences. Our chauffeur service for cruise passengers London focuses on timing, comfort, and discretion, offering luxury cruise port transfers with chauffeur.',
    alt: 'Cruise Port Chauffeur Transfers London',
    titleTag: 'Cruise Port Chauffeur Transfers London',
    link: '/services/CruisePortChauffeurTransfersLondon',
  },
  {
    id: 'london',
    image: London,
    title: 'London Day Trip Chauffeur London',
    description:
      "Discover London in comfort and style with our Chauffeur-Driven London Tours. Whether you're exploring historic landmarks, indulging in a day of shopping, or entertaining clients, our chauffeur service for London tours ensures your journey is as memorable as the destination.",
    alt: 'London Day Trip Chauffeur',
    titleTag: 'London Day Trip Chauffeur',
    link: '/services/LondonDayTripChauffeur',
  },
  {
    id: 'film-tv',
    image: Film,
    title: 'Film and TV Production Chauffeur Services London',
    description:
      "From early-morning call times to late-night wrap-ups, our Media Production Chauffeur Service London ensures your production runs smoothly. Whether it's a discreet transfer for an A-list star or moving multiple crew members between locations, our chauffeur service for film and television production London delivers reliability, comfort, and peace of mind.",
    alt: 'Film and TV Production Chauffeur Services London',
    titleTag: 'Film and TV Production Chauffeur Services London',
    link: '/services/FilmandTVProductionChauffeurServicesLondon',
  },
]

const Services = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -100px 0px' })
  const [shouldAnimate, setShouldAnimate] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      const isLargeScreen = window.innerWidth >= 768 // tablet and above
      setShouldAnimate(isLargeScreen)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.15,
      },
    },
  }

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 60,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  }

  // Create custom variants for row-based staggering
  const getCardVariants = (index: number) => {
    const rowDelay = Math.floor(index / 3) * 0.3
    const cardDelay = (index % 3) * 0.15

    return {
      hidden: {
        opacity: 0,
        y: 60,
        scale: 0.9,
      },
      visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration: 0.6,
          ease: 'easeOut' as const,
          delay: shouldAnimate ? rowDelay + cardDelay : 0,
        },
      },
    }
  }

  return (
    <div className={style.fleet}>
      <div className="container">
        <h2>Our Services</h2>
        <motion.div
          ref={ref}
          className={`${style.fleet__row} flex-wrap justify-center`}
          initial={shouldAnimate ? 'hidden' : 'visible'}
          animate={isInView || !shouldAnimate ? 'visible' : 'hidden'}
          variants={containerVariants}
        >
          {servicesConfig.map((service, index) => (
            <motion.div
              key={service.id}
              className={style.fleet__item}
              variants={getCardVariants(index)}
              initial={shouldAnimate ? 'hidden' : 'visible'}
              animate={isInView || !shouldAnimate ? 'visible' : 'hidden'}
            >
              <Link to={service.link} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className={style.fleet__card}>
                  <picture>
                    <img src={service.image} alt={service.alt} title={service.titleTag} />
                  </picture>
                  <div className={style.fleet__card__content}>
                    <strong>{service.title}</strong>
                    <p>{service.description}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  )
}
export default Services
